import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import "./ProductImageCarousel.css";

type ProductImageCarouselProps = {
  className?: string;
  imageAlt?: string;
  imageSources: string[];
  overlayText?: string | null;
};

export function ProductImageCarousel({
  className,
  imageAlt = "",
  imageSources,
  overlayText = null,
}: ProductImageCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnapCount, setScrollSnapCount] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const hasMultipleImages = imageSources.length > 1;

  const updateCarouselState = useCallback(() => {
    if (!emblaApi) return;

    setSelectedIndex(emblaApi.selectedScrollSnap());
    setScrollSnapCount(emblaApi.scrollSnapList().length);
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    updateCarouselState();
    emblaApi.on("select", updateCarouselState);
    emblaApi.on("reInit", updateCarouselState);

    return () => {
      emblaApi.off("select", updateCarouselState);
      emblaApi.off("reInit", updateCarouselState);
    };
  }, [emblaApi, updateCarouselState]);

  const handlePrevious = () => {
    emblaApi?.scrollPrev();
  };

  const handleNext = () => {
    emblaApi?.scrollNext();
  };

  const handleDotClick = (index: number) => {
    emblaApi?.scrollTo(index);
  };

  return (
    <div
      className={`react-pdp__image-carousel${className ? ` ${className}` : ""}`}
    >
      <div className="react-pdp__image-carousel-viewport" ref={emblaRef}>
        <div className="react-pdp__image-carousel-track">
          {imageSources.length ? (
            imageSources.map((imageSource, index) => (
              <div
                className="react-pdp__image-carousel-slide"
                key={`${imageSource}-${index}`}
              >
                <img
                  alt={imageAlt}
                  className="react-pdp__image-carousel-image"
                  loading={index === 0 ? "eager" : "lazy"}
                  src={imageSource}
                />
              </div>
            ))
          ) : (
            <div
              aria-hidden="true"
              className="react-pdp__image-carousel-slide react-pdp__image-carousel-slide--empty"
            />
          )}
        </div>
      </div>

      {overlayText && (
        <span className="react-pdp__image-carousel-overlay-text">
          {overlayText}
        </span>
      )}

      {hasMultipleImages ? (
        <>
          <button
            aria-label="Vorheriges Produktbild"
            className="react-pdp__image-carousel-arrow react-pdp__image-carousel-arrow--previous"
            disabled={!canScrollPrev}
            onClick={handlePrevious}
            type="button"
          >
            <span aria-hidden="true" className="material-symbols-rounded">
              chevron_left
            </span>
          </button>
          <button
            aria-label="Nächstes Produktbild"
            className="react-pdp__image-carousel-arrow react-pdp__image-carousel-arrow--next"
            disabled={!canScrollNext}
            onClick={handleNext}
            type="button"
          >
            <span aria-hidden="true" className="material-symbols-rounded">
              chevron_right
            </span>
          </button>

          <div
            aria-label="Produktbilder"
            className="react-pdp__image-carousel-dots"
            role="tablist"
          >
            {Array.from({ length: scrollSnapCount }, (_, index) => (
              <button
                aria-label={`Produktbild ${index + 1} anzeigen`}
                aria-selected={index === selectedIndex}
                className={`react-pdp__image-carousel-dot${
                  index === selectedIndex
                    ? " react-pdp__image-carousel-dot--selected"
                    : ""
                }`}
                key={index}
                onClick={() => handleDotClick(index)}
                role="tab"
                type="button"
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
